import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  collection, query, where, onSnapshot, doc, deleteField, getDoc, getDocs,
  writeBatch, Timestamp, arrayUnion, arrayRemove, Query, QuerySnapshot,
} from 'firebase/firestore';
import { subDays } from 'date-fns';
import { setDoc, updateDoc, deleteDoc, confirmarLote } from '../config/escrituras';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { usePerfil } from './PerfilContext';
import { ArticuloCompra, Casa, InvitacionCasa, ParticipanteCasa, Repeticion, Tarea, TareaHecha } from '../types';
import { nombreDeUsuario } from '../utils/usuario';
import { comprobarLimite } from '../config/planes';
import {
  casasCompartidasOrganizadas, fechaTrasHacer, mediodia, siguienteEnTurno, tareasQueSeRepiten, turnoVigente,
} from '../utils/tareas';
import { avisarAsignacion } from '../utils/avisarTareas';

/**
 * Datos del módulo Tareas: casas, sus tareas y lo hecho.
 *
 * Una casa funciona como un plan de A medias (miembros, organizadores,
 * participantes con o sin cuenta, invitación por enlace). Aquí se escuchan las
 * casas del usuario y, de cada una, sus tareas y lo hecho en los últimos
 * DIAS_HISTORIAL días: son pocas cosas y la semana común las necesita todas.
 */

/** Días de historial que se cargan (para la semana y el resumen). */
const DIAS_HISTORIAL = 60;

export type DatosCasa = Pick<Casa, 'nombre' | 'icono'>;
export type DatosTarea = Pick<Tarea, 'nombre' | 'icono' | 'zona' | 'asignadoA' | 'turno' | 'repeticion' | 'fecha' | 'hora' | 'esfuerzo' | 'notas'>;

interface TareasContextType {
  casas: Casa[];
  tareas: Tarea[];
  hechas: TareaHecha[];
  cargando: boolean;
  recargarCasas: () => void;

  /** Crea una casa contigo como primer participante; devuelve su id. */
  crearCasa: (datos: DatosCasa, otrosParticipantes: string[]) => Promise<string>;
  /** La casa de «Mis tareas», si aún no la tienes. */
  crearCasaPersonal: () => Promise<string>;
  actualizarCasa: (casaId: string, datos: Partial<DatosCasa>) => Promise<void>;
  /** Borra la casa con sus tareas e historial. */
  eliminarCasa: (casaId: string) => Promise<void>;

  anadirParticipante: (casaId: string, nombre: string) => Promise<void>;
  actualizarParticipante: (casaId: string, id: string, datos: Partial<Pick<ParticipanteCasa, 'nombre' | 'activo' | 'peque'>>) => Promise<void>;
  eliminarParticipante: (casaId: string, id: string) => Promise<void>;

  abrirInvitacion: (casa: Casa) => Promise<string>;
  cerrarInvitacion: (casa: Casa) => Promise<void>;
  leerInvitacion: (token: string) => Promise<InvitacionCasa | null>;
  unirseACasa: (invitacion: InvitacionCasa) => Promise<void>;
  reclamarParticipante: (casaId: string, participanteId: string) => Promise<void>;
  apuntarmeComoNuevo: (casaId: string, nombre: string) => Promise<void>;
  cambiarOrganizador: (casa: Casa, uid: string, organiza: boolean) => Promise<void>;
  quitarMiembro: (casa: Casa, uid: string) => Promise<void>;
  salirDeCasa: (casa: Casa) => Promise<void>;

  /** Devuelve el id de la nueva. */
  crearTarea: (casaId: string, datos: DatosTarea) => Promise<string>;
  /** Varias de golpe (plantilla). */
  crearTareas: (casaId: string, lista: DatosTarea[]) => Promise<string[]>;
  actualizarTarea: (tarea: Tarea, datos: Partial<DatosTarea> & { activa?: boolean }) => Promise<void>;
  eliminarTarea: (tarea: Tarea) => Promise<void>;
  /** Varias de una vez (pueden ser de casas distintas). */
  eliminarTareas: (lista: Tarea[]) => Promise<void>;
  /** Hecha (o saltada) por `participanteId`: queda en el historial y la tarea pasa a la siguiente fecha. */
  marcarHecha: (tarea: Tarea, participanteId: string | undefined, saltada?: boolean) => Promise<void>;
  /** Cambia solo la próxima vez que toca. */
  posponer: (tarea: Tarea, fecha: Date) => Promise<void>;
  /** Quita una del historial y devuelve la tarea a como estaba. */
  deshacer: (hecha: TareaHecha) => Promise<void>;

  /** Lista de la compra de todas tus casas. */
  compra: ArticuloCompra[];
  anadirCompra: (casaId: string, nombre: string) => Promise<void>;
  marcarComprado: (articulo: ArticuloCompra, comprado: boolean) => Promise<void>;
  /** Quita de la lista (pueden ser de casas distintas). */
  quitarCompra: (lista: ArticuloCompra[]) => Promise<void>;

  /** Si la casa va sin límites: el Plus de quien la organiza vale para todos. */
  casaConPlus: (casaId: string) => boolean;
  /** Lo de Plus que es de cada uno (calendario, Recordatorios): tu Plus o el de quien organiza alguna de tus casas. */
  conPlusTareas: boolean;
}

const TareasContext = createContext<TareasContextType | undefined>(undefined);

const nuevoId = () => doc(collection(db, 'casas')).id;
const ts = (d: Date) => Timestamp.fromDate(mediodia(d));

const casaDeFirestore = (id: string, d: any): Casa => {
  const mapa: Record<string, any> = d.participantes ?? {};
  const participantes = Object.entries(mapa)
    .map(([pid, p]) => ({
      id: pid, nombre: p.nombre ?? '', uid: p.uid ?? undefined, activo: p.activo !== false,
      peque: p.peque === true || undefined, orden: p.orden ?? 0,
    }))
    .sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre, 'es'))
    .map(({ orden, ...p }) => p);
  return {
    id,
    nombre: d.nombre ?? '',
    icono: d.icono || '🏠',
    personal: d.personal === true,
    creadorUid: d.creadorUid,
    miembrosUids: d.miembrosUids ?? [],
    organizadoresUids: d.organizadoresUids ?? [],
    participantes,
    invitacionToken: d.invitacionToken || undefined,
    plusOrganizador: d.plusOrganizador === true,
    createdAt: d.createdAt?.toDate() ?? new Date(),
    updatedAt: d.updatedAt?.toDate() ?? d.createdAt?.toDate() ?? new Date(),
  };
};

const repeticionDe = (r: any): Repeticion => ({
  tipo: ['una', 'dias', 'semanas', 'meses'].includes(r?.tipo) ? r.tipo : 'una',
  cada: typeof r?.cada === 'number' && r.cada > 0 ? r.cada : 1,
  diasSemana: Array.isArray(r?.diasSemana) ? r.diasSemana : undefined,
});

const tareaDeFirestore = (casaId: string, id: string, d: any): Tarea => ({
  id,
  casaId,
  nombre: d.nombre ?? '',
  icono: d.icono || '🧹',
  zona: d.zona || undefined,
  asignadoA: d.asignadoA || undefined,
  turno: Array.isArray(d.turno) && d.turno.length > 1 ? d.turno : undefined,
  repeticion: repeticionDe(d.repeticion),
  fecha: d.fecha?.toDate() ?? new Date(),
  hora: d.hora || undefined,
  esfuerzo: [1, 2, 3].includes(d.esfuerzo) ? d.esfuerzo : 1,
  notas: d.notas || undefined,
  activa: d.activa !== false,
  creadoPorUid: d.creadoPorUid,
  createdAt: d.createdAt?.toDate() ?? new Date(),
  updatedAt: d.updatedAt?.toDate() ?? d.createdAt?.toDate() ?? new Date(),
});

const hechaDeFirestore = (casaId: string, id: string, d: any): TareaHecha => ({
  id,
  casaId,
  tareaId: d.tareaId,
  nombreTarea: d.nombreTarea ?? '',
  icono: d.icono || '🧹',
  participanteId: d.participanteId || undefined,
  uid: d.uid,
  fecha: d.fecha?.toDate() ?? new Date(),
  hechaEn: d.hechaEn?.toDate() ?? new Date(),
  saltada: d.saltada === true,
  fechaAnterior: d.fechaAnterior?.toDate() ?? d.fecha?.toDate() ?? new Date(),
  activaAntes: d.activaAntes !== false,
  asignadoAntes: d.asignadoAntes || undefined,
});

const compraDeFirestore = (casaId: string, id: string, d: any): ArticuloCompra => ({
  id,
  casaId,
  nombre: d.nombre ?? '',
  comprado: d.comprado === true,
  creadoPorUid: d.creadoPorUid,
  createdAt: d.createdAt?.toDate() ?? new Date(),
  compradoEn: d.compradoEn?.toDate() ?? undefined,
});

const tareaAFirestore = (t: Partial<DatosTarea>) => {
  const out: Record<string, any> = {};
  if ('nombre' in t) out.nombre = (t.nombre ?? '').trim();
  if ('icono' in t) out.icono = t.icono || '🧹';
  // Vaciar un opcional es escribir null, como en el resto de la app
  if ('zona' in t) out.zona = t.zona || null;
  if ('asignadoA' in t) out.asignadoA = t.asignadoA || null;
  if ('turno' in t) out.turno = t.turno && t.turno.length > 1 ? t.turno : null;
  if ('repeticion' in t && t.repeticion) {
    out.repeticion = {
      tipo: t.repeticion.tipo,
      cada: Math.max(1, Math.round(t.repeticion.cada || 1)),
      ...(t.repeticion.tipo === 'semanas' ? { diasSemana: t.repeticion.diasSemana ?? [] } : {}),
    };
  }
  if ('fecha' in t && t.fecha) out.fecha = ts(t.fecha);
  if ('hora' in t) out.hora = t.hora || null;
  if ('esfuerzo' in t) out.esfuerzo = t.esfuerzo ?? 1;
  if ('notas' in t) out.notas = t.notas?.trim() || null;
  return out;
};

export const TareasProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { tieneModulo, esPlus } = usePerfil();
  // Como en A medias: un listener que las reglas rechazan se muere, así que al
  // activarse el módulo hay que volver a suscribirse.
  const conModulo = tieneModulo('tareas');
  const [casas, setCasas] = useState<Casa[]>([]);
  // Para quién son las casas que hay en memoria. Se compara en el mismo render
  // (no en un efecto), porque los efectos de las páginas corren antes que los
  // de este contexto: con un «cargando» puesto desde un efecto, la semana veía
  // un instante «ya cargado y sin casas» y creaba otra «Mis tareas».
  const clave = user && conModulo ? user.uid : '';
  const [casasDe, setCasasDe] = useState<string | null>(null);
  const cargandoCasas = casasDe !== clave;
  const [recarga, setRecarga] = useState(0);
  const [tareasPorCasa, setTareasPorCasa] = useState<Record<string, Tarea[]>>({});
  const [hechasPorCasa, setHechasPorCasa] = useState<Record<string, TareaHecha[]>>({});
  const [compraPorCasa, setCompraPorCasa] = useState<Record<string, ArticuloCompra[]>>({});

  useEffect(() => {
    if (!user || !conModulo) {
      setCasas([]);
      setCasasDe('');
      return;
    }
    const q = query(collection(db, 'casas'), where('miembrosUids', 'array-contains', user.uid));
    const unsub = onSnapshot(q, snap => {
      const lista = snap.docs.map(d => casaDeFirestore(d.id, d.data()));
      // La personal primero; luego por antigüedad, que es lo estable
      lista.sort((a, b) => Number(b.personal) - Number(a.personal) || a.createdAt.getTime() - b.createdAt.getTime());
      setCasas(lista);
      setCasasDe(user.uid);
    }, err => {
      console.warn('Tareas no disponible:', err.code);
      setCasas([]);
      setCasasDe(user.uid);
    });
    return unsub;
  }, [user, conModulo, recarga]);

  // Tareas e historial de cada casa. Se vuelve a suscribir solo si cambia qué casas hay.
  const idsCasas = casas.map(c => c.id).join(',');
  useEffect(() => {
    const ids = idsCasas ? idsCasas.split(',') : [];
    setTareasPorCasa(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => ids.includes(k))));
    setHechasPorCasa(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => ids.includes(k))));
    setCompraPorCasa(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => ids.includes(k))));
    const desde = Timestamp.fromDate(subDays(new Date(), DIAS_HISTORIAL));
    // Una casa recién creada llega aquí desde la caché antes de existir en el
    // servidor: las reglas (que leen la casa) rechazan el listener y este se
    // muere. Se reintenta unas veces, que es lo que tarda en subir la casa.
    let vivo = true;
    const escuchar = (q: Query, alLlegar: (snap: QuerySnapshot) => void, aviso: string) => {
      let unsub = () => {};
      let intentos = 0;
      const abrir = () => {
        unsub = onSnapshot(q, snap => { intentos = 0; alLlegar(snap); }, err => {
          if (err.code === 'permission-denied' && intentos < 5 && vivo) {
            intentos++;
            setTimeout(() => { if (vivo) abrir(); }, 1000 * intentos);
          } else console.warn(aviso, err.code);
        });
      };
      abrir();
      return () => unsub();
    };
    const unsubs = ids.flatMap(casaId => [
      escuchar(collection(db, 'casas', casaId, 'tareas'), snap => {
        setTareasPorCasa(prev => ({ ...prev, [casaId]: snap.docs.map(s => tareaDeFirestore(casaId, s.id, s.data())) }));
      }, 'Tareas de la casa no disponibles:'),
      escuchar(query(collection(db, 'casas', casaId, 'hechas'), where('hechaEn', '>=', desde)), snap => {
        setHechasPorCasa(prev => ({ ...prev, [casaId]: snap.docs.map(s => hechaDeFirestore(casaId, s.id, s.data())) }));
      }, 'Historial de la casa no disponible:'),
      escuchar(collection(db, 'casas', casaId, 'compra'), snap => {
        setCompraPorCasa(prev => ({ ...prev, [casaId]: snap.docs.map(s => compraDeFirestore(casaId, s.id, s.data())) }));
      }, 'Lista de la compra no disponible:'),
    ]);
    return () => { vivo = false; unsubs.forEach(u => u()); };
  }, [idsCasas]);

  const tareas = useMemo(() => Object.values(tareasPorCasa).flat(), [tareasPorCasa]);
  const hechas = useMemo(
    () => Object.values(hechasPorCasa).flat().sort((a, b) => b.hechaEn.getTime() - a.hechaEn.getTime()),
    [hechasPorCasa]);
  const compra = useMemo(() => Object.values(compraPorCasa).flat(), [compraPorCasa]);
  const cargando = cargandoCasas || casas.some(c => !tareasPorCasa[c.id]);

  // Quien creó la casa (y la sigue organizando) deja dicho en ella si tiene
  // Plus: así los demás saben que va sin límites, porque el Plus de otra
  // cuenta no se puede leer. Solo él, para que no se pisen dos organizadores.
  useEffect(() => {
    if (!user) return;
    casas
      .filter(c => c.organizadoresUids.includes(user.uid) && c.creadorUid === user.uid && c.plusOrganizador !== esPlus)
      .forEach(c => updateDoc(doc(db, 'casas', c.id), { plusOrganizador: esPlus })
        .catch(e => console.warn('No se pudo apuntar el Plus de la casa', e)));
  }, [casas, esPlus, user]);

  const tocarCasa = (casaId: string, datos: Record<string, any>) =>
    updateDoc(doc(db, 'casas', casaId), { ...datos, updatedAt: Timestamp.now() });

  // ── Casas ────────────────────────────────────────────────────────────────
  const nuevaCasa = async (datos: DatosCasa, otros: string[], personal: boolean) => {
    if (!user) throw new Error('Sesión no iniciada');
    // La personal tiene id fijo: aunque dos pantallas la creen a la vez, sale una
    const ref = personal ? doc(db, 'casas', `personal_${user.uid}`) : doc(collection(db, 'casas'));
    const participantes: Record<string, any> = {
      [nuevoId()]: { nombre: nombreDeUsuario(user), uid: user.uid, activo: true, orden: 0 },
    };
    otros.map(n => n.trim()).filter(Boolean).forEach((nombre, i) => {
      participantes[nuevoId()] = { nombre, activo: true, orden: i + 1 };
    });
    await setDoc(ref, {
      nombre: datos.nombre.trim(),
      icono: datos.icono,
      personal,
      creadorUid: user.uid,
      miembrosUids: [user.uid],
      organizadoresUids: [user.uid],
      participantes,
      plusOrganizador: esPlus,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return ref.id;
  };

  const crearCasa: TareasContextType['crearCasa'] = async (datos, otros) => {
    comprobarLimite('casasCompartidas', casasCompartidasOrganizadas(casas, user?.uid), esPlus);
    return nuevaCasa(datos, otros, false);
  };

  const crearCasaPersonal = async () => {
    if (!user) throw new Error('Sesión no iniciada');
    // Si ya existe (otro dispositivo la creó), no se pisa
    const existe = await getDoc(doc(db, 'casas', `personal_${user.uid}`)).then(s => s.exists()).catch(() => false);
    if (existe) return `personal_${user.uid}`;
    return nuevaCasa({ nombre: 'Mis tareas', icono: '🙋' }, [], true);
  };

  const actualizarCasa: TareasContextType['actualizarCasa'] = async (casaId, datos) => {
    const out: Record<string, any> = {};
    if ('nombre' in datos) out.nombre = (datos.nombre ?? '').trim();
    if ('icono' in datos) out.icono = datos.icono;
    await tocarCasa(casaId, out);
    // La invitación enseña nombre e icono a quien aún no ve la casa
    const token = casas.find(c => c.id === casaId)?.invitacionToken;
    if (token && ('nombre' in datos || 'icono' in datos)) {
      await updateDoc(doc(db, 'invitacionesCasa', token), {
        ...('nombre' in datos ? { nombreCasa: out.nombre } : {}),
        ...('icono' in datos ? { iconoCasa: datos.icono } : {}),
      });
    }
  };

  const eliminarCasa = async (casaId: string) => {
    // Firestore no borra subcolecciones: se va a mano, y la casa lo último
    // porque las reglas de tareas e invitación la consultan.
    const token = casas.find(c => c.id === casaId)?.invitacionToken;
    if (token) await deleteDoc(doc(db, 'invitacionesCasa', token));
    for (const sub of ['tareas', 'hechas', 'compra']) {
      const snap = await getDocs(collection(db, 'casas', casaId, sub));
      for (let i = 0; i < snap.docs.length; i += 450) {
        const batch = writeBatch(db);
        snap.docs.slice(i, i + 450).forEach(d => batch.delete(d.ref));
        await confirmarLote(batch);
      }
    }
    await deleteDoc(doc(db, 'casas', casaId));
  };

  // ── Participantes ────────────────────────────────────────────────────────
  const anadirParticipante = (casaId: string, nombre: string) =>
    // Date.now() como orden: el nuevo sale el último sin leer a los demás
    tocarCasa(casaId, { [`participantes.${nuevoId()}`]: { nombre: nombre.trim(), activo: true, orden: Date.now() } });

  const actualizarParticipante: TareasContextType['actualizarParticipante'] = async (casaId, id, datos) => {
    const out: Record<string, any> = {};
    if (datos.nombre !== undefined) out[`participantes.${id}.nombre`] = datos.nombre.trim();
    if (datos.activo !== undefined) out[`participantes.${id}.activo`] = datos.activo;
    if (datos.peque !== undefined) out[`participantes.${id}.peque`] = datos.peque;
    await tocarCasa(casaId, out);
  };

  const eliminarParticipante = (casaId: string, id: string) =>
    tocarCasa(casaId, { [`participantes.${id}`]: deleteField() });

  // ── Invitaciones y miembros ──────────────────────────────────────────────
  const abrirInvitacion = async (casa: Casa) => {
    if (!user) throw new Error('Sesión no iniciada');
    const ref = doc(collection(db, 'invitacionesCasa'));
    await setDoc(ref, {
      casaId: casa.id,
      nombreCasa: casa.nombre,
      iconoCasa: casa.icono,
      creadaPorUid: user.uid,
      creadaPorNombre: casa.participantes.find(p => p.uid === user.uid)?.nombre ?? nombreDeUsuario(user),
      abierta: true,
      createdAt: Timestamp.now(),
    });
    await tocarCasa(casa.id, { invitacionToken: ref.id });
    if (casa.invitacionToken) await deleteDoc(doc(db, 'invitacionesCasa', casa.invitacionToken)).catch(() => {});
    return ref.id;
  };

  const cerrarInvitacion = async (casa: Casa) => {
    await tocarCasa(casa.id, { invitacionToken: null });
    if (casa.invitacionToken) await deleteDoc(doc(db, 'invitacionesCasa', casa.invitacionToken)).catch(() => {});
  };

  const leerInvitacion = async (token: string): Promise<InvitacionCasa | null> => {
    try {
      const snap = await getDoc(doc(db, 'invitacionesCasa', token));
      if (!snap.exists()) return null;
      const d = snap.data();
      return {
        token, casaId: d.casaId, nombreCasa: d.nombreCasa ?? '', iconoCasa: d.iconoCasa || '🏠',
        creadaPorUid: d.creadaPorUid, creadaPorNombre: d.creadaPorNombre ?? '', abierta: d.abierta === true,
      };
    } catch (e) {
      console.warn('No se pudo leer la invitación', e);
      return null;
    }
  };

  const unirseACasa = async (inv: InvitacionCasa) => {
    if (!user) throw new Error('Sesión no iniciada');
    await updateDoc(doc(db, 'casas', inv.casaId), {
      miembrosUids: arrayUnion(user.uid), invitacionUsada: inv.token, updatedAt: Timestamp.now(),
    });
  };

  const reclamarParticipante = async (casaId: string, participanteId: string) => {
    if (!user) throw new Error('Sesión no iniciada');
    await tocarCasa(casaId, { [`participantes.${participanteId}.uid`]: user.uid, reclamado: participanteId });
  };

  const apuntarmeComoNuevo = async (casaId: string, nombre: string) => {
    if (!user) throw new Error('Sesión no iniciada');
    const id = nuevoId();
    await tocarCasa(casaId, {
      [`participantes.${id}`]: { nombre: nombre.trim(), uid: user.uid, activo: true, orden: Date.now() },
      reclamado: id,
    });
  };

  const cambiarOrganizador = (casa: Casa, uid: string, organiza: boolean) =>
    tocarCasa(casa.id, { organizadoresUids: organiza ? arrayUnion(uid) : arrayRemove(uid) });

  // Quien sale deja de ver la casa; su participante y su historial se quedan
  const quitarMiembro = (casa: Casa, uid: string) =>
    tocarCasa(casa.id, { miembrosUids: arrayRemove(uid), organizadoresUids: arrayRemove(uid) });

  const salirDeCasa = async (casa: Casa) => {
    if (!user) throw new Error('Sesión no iniciada');
    await tocarCasa(casa.id, { miembrosUids: arrayRemove(user.uid) });
  };

  // ── Tareas ───────────────────────────────────────────────────────────────
  const casaConPlus = (casaId: string) => esPlus || casas.find(c => c.id === casaId)?.plusOrganizador === true;

  const crearTareas: TareasContextType['crearTareas'] = async (casaId, lista) => {
    if (!user) throw new Error('Sesión no iniciada');
    const nuevasQueSeRepiten = lista.filter(t => t.repeticion.tipo !== 'una').length;
    if (nuevasQueSeRepiten) {
      comprobarLimite('tareasPeriodicas', tareasQueSeRepiten(tareas, casaId), casaConPlus(casaId), nuevasQueSeRepiten);
    }
    const batch = writeBatch(db);
    const ids = lista.map(t => {
      const ref = doc(collection(db, 'casas', casaId, 'tareas'));
      batch.set(ref, {
        ...tareaAFirestore(t), activa: true,
        creadoPorUid: user.uid, createdAt: Timestamp.now(), updatedAt: Timestamp.now(),
      });
      return ref.id;
    });
    await confirmarLote(batch);
    return ids;
  };

  const actualizarTarea: TareasContextType['actualizarTarea'] = async (tarea, datos) => {
    // Pasar a repetirse (o reactivar una que se repite) cuenta para el límite
    const seRepetira = (datos.repeticion ?? tarea.repeticion).tipo !== 'una';
    const yaContaba = tarea.activa && tarea.repeticion.tipo !== 'una';
    const activa = datos.activa ?? tarea.activa;
    if (seRepetira && activa && !yaContaba) {
      comprobarLimite('tareasPeriodicas', tareasQueSeRepiten(tareas, tarea.casaId), casaConPlus(tarea.casaId));
    }
    const { activa: _a, ...resto } = datos;
    await updateDoc(doc(db, 'casas', tarea.casaId, 'tareas', tarea.id), {
      ...tareaAFirestore(resto),
      ...(datos.activa !== undefined ? { activa: datos.activa } : {}),
      updatedAt: Timestamp.now(),
    });
  };

  const eliminarTarea = (tarea: Tarea) => deleteDoc(doc(db, 'casas', tarea.casaId, 'tareas', tarea.id));

  // Por lotes de 400, por debajo del límite de 500 escrituras de Firestore
  const eliminarTareas: TareasContextType['eliminarTareas'] = async lista => {
    for (let i = 0; i < lista.length; i += 400) {
      const batch = writeBatch(db);
      lista.slice(i, i + 400).forEach(t => batch.delete(doc(db, 'casas', t.casaId, 'tareas', t.id)));
      await confirmarLote(batch);
    }
  };

  // Historial y tarea en el mismo batch: o las dos cosas o ninguna
  const marcarHecha: TareasContextType['marcarHecha'] = async (tarea, participanteId, saltada = false) => {
    if (!user) throw new Error('Sesión no iniciada');
    const siguiente = fechaTrasHacer(tarea);
    // Por turnos: hecha, le toca al siguiente. Saltada, le sigue tocando a quien le tocaba.
    const turno = turnoVigente(tarea.turno, casas.find(c => c.id === tarea.casaId));
    const siguienteTurno = siguiente && !saltada && turno.length > 1 ? siguienteEnTurno(turno, tarea.asignadoA) : undefined;
    const batch = writeBatch(db);
    batch.set(doc(collection(db, 'casas', tarea.casaId, 'hechas')), {
      tareaId: tarea.id,
      nombreTarea: tarea.nombre,
      icono: tarea.icono,
      participanteId: participanteId ?? null,
      uid: user.uid,
      fecha: ts(tarea.fecha),
      hechaEn: Timestamp.now(),
      saltada,
      fechaAnterior: ts(tarea.fecha),
      activaAntes: tarea.activa,
      ...(siguienteTurno ? { asignadoAntes: tarea.asignadoA ?? null } : {}),
    });
    batch.update(doc(db, 'casas', tarea.casaId, 'tareas', tarea.id), siguiente
      ? { fecha: ts(siguiente), ...(siguienteTurno ? { asignadoA: siguienteTurno } : {}), updatedAt: Timestamp.now() }
      : { activa: false, updatedAt: Timestamp.now() });
    await confirmarLote(batch);
    if (siguienteTurno && siguienteTurno !== tarea.asignadoA) {
      avisarAsignacion({ casaId: tarea.casaId, tareaId: tarea.id, motivo: 'turno' });
    }
  };

  const posponer = (tarea: Tarea, fecha: Date) =>
    updateDoc(doc(db, 'casas', tarea.casaId, 'tareas', tarea.id), { fecha: ts(fecha), updatedAt: Timestamp.now() });

  const deshacer = async (hecha: TareaHecha) => {
    const batch = writeBatch(db);
    batch.delete(doc(db, 'casas', hecha.casaId, 'hechas', hecha.id));
    // Si la tarea se ha borrado entretanto, solo se quita del historial
    if (tareas.some(t => t.id === hecha.tareaId && t.casaId === hecha.casaId)) {
      batch.update(doc(db, 'casas', hecha.casaId, 'tareas', hecha.tareaId), {
        fecha: ts(hecha.fechaAnterior), activa: hecha.activaAntes, updatedAt: Timestamp.now(),
        // Si había pasado el turno, vuelve a quien le tocaba
        ...(hecha.asignadoAntes !== undefined ? { asignadoA: hecha.asignadoAntes || null } : {}),
      });
    }
    await confirmarLote(batch);
  };

  // ── Lista de la compra ───────────────────────────────────────────────────
  const anadirCompra = async (casaId: string, nombre: string) => {
    if (!user) throw new Error('Sesión no iniciada');
    await setDoc(doc(collection(db, 'casas', casaId, 'compra')), {
      nombre: nombre.trim(), comprado: false, creadoPorUid: user.uid, createdAt: Timestamp.now(),
    });
  };

  const marcarComprado = (a: ArticuloCompra, comprado: boolean) =>
    updateDoc(doc(db, 'casas', a.casaId, 'compra', a.id), { comprado, compradoEn: comprado ? Timestamp.now() : null });

  const quitarCompra = async (lista: ArticuloCompra[]) => {
    for (let i = 0; i < lista.length; i += 400) {
      const batch = writeBatch(db);
      lista.slice(i, i + 400).forEach(a => batch.delete(doc(db, 'casas', a.casaId, 'compra', a.id)));
      await confirmarLote(batch);
    }
  };

  return (
    <TareasContext.Provider value={{
      casas, tareas, hechas, cargando, recargarCasas: () => setRecarga(r => r + 1),
      crearCasa, crearCasaPersonal, actualizarCasa, eliminarCasa,
      anadirParticipante, actualizarParticipante, eliminarParticipante,
      abrirInvitacion, cerrarInvitacion, leerInvitacion, unirseACasa,
      reclamarParticipante, apuntarmeComoNuevo, cambiarOrganizador, quitarMiembro, salirDeCasa,
      crearTarea: (casaId, datos) => crearTareas(casaId, [datos]).then(ids => ids[0]),
      crearTareas, actualizarTarea, eliminarTarea, eliminarTareas, marcarHecha, posponer, deshacer,
      compra, anadirCompra, marcarComprado, quitarCompra,
      casaConPlus, conPlusTareas: esPlus || casas.some(c => !c.personal && c.plusOrganizador),
    }}>
      {children}
    </TareasContext.Provider>
  );
};

export const useTareas = () => {
  const ctx = useContext(TareasContext);
  if (!ctx) throw new Error('useTareas debe usarse dentro de TareasProvider');
  return ctx;
};
